import { LimitWarning } from './LimitWarning';
import React, { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { formatINR } from '@/lib/format';
import { ShieldCheck, AlertCircle, CheckCircle2, IndianRupee, Diamond, BadgePercent } from 'lucide-react';

const POLICY_ITEMS = [
    { key: 'refund', href: '/policies/refund', label: 'Refund Policy', prefix: 'I have read and accept the ', suffix: '.' },
    { key: 'privacy', href: '/policies/privacy', label: 'Privacy Policy', prefix: 'I accept the ', suffix: '.' },
    { key: 'fairuse', href: '/policies/fair-use', label: 'Fair Use Policy', prefix: 'I accept the ', suffix: ', understand digital goods are non-reversible, and confirm this is for entertainment use only.' },
];

function PolicyCheckboxes({ policy, onChange }) {
    return (
        <div className="space-y-2">
            {POLICY_ITEMS.map(({ key, href, label, prefix, suffix }) => (
                <label key={key} className="flex items-start gap-2 text-[13px] cursor-pointer">
                    <Checkbox checked={!!policy[key]} onCheckedChange={(v) => onChange(key, !!v)} data-testid={`recharge-policy-${key}-checkbox`} />
                    <span>{prefix}<a className="text-[#1F9D8A] underline" href={href} target="_blank" rel="noreferrer">{label}</a>{suffix}</span>
                </label>
            ))}
        </div>
    );
}

/**
 * CustomRechargeCard — user enters a ₹ amount and sees how many in-app diamonds
 * they will receive (₹ ÷ rate × multiplier). Mirrors reseller preview UX.
 */
export function CustomRechargeCard({
    platformMeta, platformKey, rate, multiplier, defaultRate,
    amount, onAmountChange,
    inAppId, onInAppIdChange, onInAppIdBlur,
    verifiable, verifying, recipientInfo, verifyError,
    policy, onPolicyChange, allPoliciesAccepted,
    gateways = [], gateway, onGatewayChange,
    loading, onPay, maxLimit = 15000,
}) {
    const amt = parseFloat(amount) || 0;
    const quote = useMemo(() => {
        const base = rate > 0 ? amt / rate : 0;
        const inApp = Math.round(base * (multiplier || 1));
        return { base, inApp };
    }, [amt, rate, multiplier]);
    const savings = useMemo(() => {
        const dr = Number(defaultRate) || 0;
        if (!(dr > rate) || amt <= 0 || rate <= 0) return null;
        const standard = quote.base * dr;   // ₹ the same diamonds cost at the default rate
        const inr = standard - amt;         // ₹ saved vs the standard price
        const pct = (inr / standard) * 100;
        const pctLabel = pct < 10 ? `${pct.toFixed(1)}%` : `${pct.toFixed(0)}%`;
        return { inr, pct, pctLabel, standard };
    }, [defaultRate, rate, amt, quote.base]);
    const payGateway = gateways.find((g) => g.key === gateway);
    const gatewayNotReady = payGateway && !payGateway.implemented;
    const overLimit = amt > maxLimit;
    const amtInvalid = amt > 0 && amt < 50;
    const canPay = amt >= 50 && !overLimit && quote.inApp > 0 && inAppId.trim() && allPoliciesAccepted && (!verifiable || recipientInfo) && !gatewayNotReady;

    return (
        <Card className="mt-6 p-4 sm:p-5" data-testid="custom-recharge-card">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                <IndianRupee className="h-3.5 w-3.5" /> Enter amount
            </div>
            <h3 className="font-display text-lg font-semibold mt-1">Recharge by amount</h3>
            <p className="text-sm text-muted-foreground">Type how much you want to spend — we show the diamonds you'll receive instantly.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                    <Label htmlFor="custom-amount">Amount (₹)</Label>
                    <Input
                        id="custom-amount" type="number" min="50" max={Number.isFinite(maxLimit) ? maxLimit : undefined} step="1"
                        inputMode="numeric" placeholder="e.g. 500"
                        value={amount}
                        onChange={(e) => onAmountChange(e.target.value)}
                        className={amtInvalid ? 'border-rose-500 focus-visible:ring-rose-500' : ''}
                        aria-invalid={amtInvalid}
                        data-testid="custom-amount-input"
                    />
                    {amtInvalid && (
                        <div className="text-xs text-rose-600 font-medium" data-testid="custom-amount-error">Minimum recharge is ₹50</div>
                    )}
                    <div className="text-xs text-muted-foreground">Rate: ₹{Number(rate).toFixed(2)} per diamond{multiplier > 1 ? ` · ×${multiplier} in-app` : ''}</div>
                </div>

                <div className="rounded-xl border border-border p-3 bg-secondary/40" data-testid="custom-recharge-summary">
                    <div className="text-xs text-muted-foreground">You will receive</div>
                    <div className="mt-1 flex items-baseline gap-2">
                        <Diamond className="h-5 w-5 text-amber-500" />
                        <span className="font-display text-2xl font-semibold tabular-nums text-foreground" data-testid="custom-inapp-diamonds">
                            {quote.inApp.toLocaleString()}
                        </span>
                        <span className="text-xs uppercase text-muted-foreground">{platformMeta?.unit || 'diamonds'}</span>
                    </div>
                    {multiplier > 1 && amt > 0 && (
                        <div className="text-[11px] text-muted-foreground mt-1" data-testid="custom-calc-breakdown">
                            ₹{amt.toLocaleString()} ÷ ₹{Number(rate).toFixed(2)} = {quote.base.toLocaleString(undefined, { maximumFractionDigits: 2 })} 💎 × {multiplier}
                        </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                        {savings && (
                            <span className="text-sm text-muted-foreground line-through" data-testid="custom-standard-price">{formatINR(savings.standard)}</span>
                        )}
                        <span className="text-lg font-display font-semibold tabular-nums">{formatINR(amt)}</span>
                    </div>
                    {savings && (
                        <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800" data-testid="custom-savings-badge">
                            <BadgePercent className="h-3.5 w-3.5" /> You save {formatINR(savings.inr)} ({savings.pctLabel}) vs standard price
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-4 space-y-2">
                <Label htmlFor="custom-inappid">Your {platformMeta?.name || ''} in-app ID</Label>
                <Input
                    id="custom-inappid" data-testid="custom-inapp-id-input"
                    placeholder="Enter your in-app ID exactly"
                    value={inAppId}
                    onChange={(e) => onInAppIdChange(e.target.value)}
                    onBlur={() => onInAppIdBlur && onInAppIdBlur()}
                />
                {verifiable && verifying && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="custom-verifying">
                        <span className="h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin" /> Fetching account…
                    </div>
                )}
                {verifiable && !verifying && recipientInfo && (
                    <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2" data-testid="custom-recipient-info">
                        {recipientInfo.avatar ? <img src={recipientInfo.avatar} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="h-8 w-8 rounded-full bg-emerald-200" />}
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-emerald-900 truncate">{recipientInfo.nick || 'Account found'}</div>
                            <div className="text-[11px] text-emerald-700 font-mono">ID: {recipientInfo.vid || inAppId}</div>
                        </div>
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 ml-auto flex-shrink-0" />
                    </div>
                )}
                {verifiable && !verifying && !recipientInfo && verifyError && (
                    <div className="text-xs text-rose-600" data-testid="custom-verify-error">{verifyError}</div>
                )}
            </div>

            {gateways.length > 1 && (
                <div className="mt-5" data-testid="custom-gateway-selector">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pay using</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {gateways.map((g) => (
                            <button key={g.key} type="button" onClick={() => onGatewayChange && onGatewayChange(g.key)}
                                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${gateway === g.key ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-foreground border-border hover:border-[#0E2A47]/40'}`}
                                data-testid={`custom-gateway-option-${g.key}`}>
                                {g.name}{!g.implemented && <span className="ml-1.5 text-[10px] uppercase opacity-70">Coming soon</span>}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-5 p-4 rounded-xl border border-border bg-secondary/30">
                <div className="flex items-start gap-2 text-[13px] mb-3">
                    <AlertCircle className="h-4 w-4 mt-0.5 text-[#B45309]" />
                    <div className="text-foreground/85">Digital goods are non-reversible once credited. This service is for entertainment use only.</div>
                </div>
                <PolicyCheckboxes policy={policy} onChange={onPolicyChange} />
            </div>

            {overLimit && <LimitWarning testid="custom-limit-warning" amount={amt} />}

            <Button disabled={loading || !canPay} onClick={onPay}
                className="mt-5 w-full bg-[#0E2A47] hover:bg-[#0B223A] disabled:opacity-50" data-testid="custom-pay-button">
                {loading ? 'Processing...' : (
                    <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Pay {formatINR(amt)} with {payGateway ? payGateway.name : 'Razorpay'}</span>
                )}
            </Button>
        </Card>
    );
}

export default CustomRechargeCard;
